'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { QueryApi } from '@/features/query/api';
import { useGetPageUrl } from '@/schema/useGetPageUrl';
import { PAGINATION_LIMIT } from '@/shared/config/constants/api';
import { Link } from '@/shared/i18n/navigation';
import { Button } from '@/shared/ui/button';
import { EmptyContainer } from '@/shared/ui/empty-container';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import { FavoriteItemsModalItem } from './favorite-items-modal-item';
import './favorite-items-modal.scss';

type Props = ModalProps & {
    children?: ReactNode;
};

type FavoriteItemsModalContextValue = {
    favoriteItemsQuery: ReturnType<
        typeof useQuery<
            Awaited<
                ReturnType<NonNullable<ReturnType<typeof QueryApi.Customer.queries.FavoriteItems>['queryFn']>>
            >
        >
    >;
    itemCount: number;
    getPageUrl: ReturnType<typeof useGetPageUrl>;
    onClose: ModalProps['onClose'];
};

const FavoriteItemsModalContext = createContext<FavoriteItemsModalContextValue | null>(null);

const useFavoriteItemsModalContext = () => {
    const context = useContext(FavoriteItemsModalContext);
    if (!context) {
        throw new Error('FavoriteItemsModal components must be used within FavoriteItemsModalWrapper.');
    }
    return context;
};

/** Provider that only provides context and renders children (Modal prefab). */
export const FavoriteItemsModalProvider = ({ children, onClose }: Props) => {
    const getPageUrl = useGetPageUrl();
    // TODO: Infinite scroll
    const favoriteItemsQuery = useQuery(
        QueryApi.Customer.queries.FavoriteItems({ offset: 0, limit: PAGINATION_LIMIT }),
    );

    const itemCount = favoriteItemsQuery.data?.items?.length ?? 0;
    return (
        <FavoriteItemsModalContext.Provider value={{ favoriteItemsQuery, itemCount, getPageUrl, onClose }}>
            {children}
        </FavoriteItemsModalContext.Provider>
    );
};

export const FavoriteItemsModalWrapper = ({ children, onClose, ...props }: Props) => {
    return (
        <FavoriteItemsModalProvider onClose={onClose} {...props}>
            <Modal
                {...props}
                onClose={onClose}
                wrapperProps={{
                    className: 'favorite-items-modal',
                    containerProps: {
                        className: 'favorite-items-modal__container',
                    },
                    'data-testid': 'favorite-items-modal',
                }}
            >
                {children}
            </Modal>
        </FavoriteItemsModalProvider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const FavoriteItemsModalTitle = () => {
    const t = useTranslations();
    return <>{t(`FAVORITE_ITEMS_MODAL.TITLE`)}</>;
};

type ContentProps = { children?: ReactNode };
const FavoriteItemsModalContent = ({ children }: ContentProps) => {
    const t = useTranslations();
    const { favoriteItemsQuery, itemCount } = useFavoriteItemsModalContext();
    return (
        <>
            {itemCount === 0 && (
                <EmptyContainer
                    icon={(svgProps) => <Icon name="heart" {...svgProps} />}
                    text={t('FAVORITE_ITEMS_MODAL.EMPTY_MESSAGE')}
                />
            )}
            {favoriteItemsQuery.data?.items?.map((item, index) => (
                <FavoriteItemsModalItem key={index} {...item} />
            ))}
            {children}
        </>
    );
};

type ButtonProps = ComponentProps<typeof Layout>;
const FavoriteItemsModalButton = ({ media, ...props }: ButtonProps) => {
    const t = useTranslations();
    const { itemCount, getPageUrl } = useFavoriteItemsModalContext();

    const handleFindItems = () => {
        ModalService.hideAll();
    };

    return (
        <Layout element={Button} asChild media={media} {...props}>
            <Link
                href={getPageUrl('home')!}
                data-testid="favorite-items-modal-save-button"
                onClick={handleFindItems}
            >
                {t(`FAVORITE_ITEMS_MODAL.BUTTON_${itemCount ? 'ADD' : 'FIND'}`)}
            </Link>
        </Layout>
    );
};

export { FavoriteItemsModalWrapper as FavoriteItemsModal };

export {
    FavoriteItemsModalWrapper as Wrapper,
    FavoriteItemsModalProvider as Provider,
    FavoriteItemsModalTitle as Title,
    FavoriteItemsModalContent as Content,
    FavoriteItemsModalButton as Button,
    FavoriteItemsModalItem as Item,
};
